import { ReactNode, createContext, useContext } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Which catalog shell we are in (legacy vs add-items) and, for add-items, which tab matches the URL.
 * Row identity and `selectedItems` keys are handled by `getItemUid` / checkbox `selectionKey`, not this module.
 */

/**
 * Whether catalog UI runs under the legacy property-catalog surfaces,
 * add-items-to-order (`/orders/add-items/*`), or add-items-to-list
 * (`/catalog/:propertyId/lists/:listId/add-items/*`).
 */
export type CatalogFlow = 'legacy' | 'orders-add-items' | 'lists-add-items';

/**
 * Sub-surface within add-items: property catalog (categories), inventory, or RE-OPS marketplace.
 * Derived from `location.pathname` via `getCurrentTabFromPathname`.
 */
export type AddItemsCatalogTab =
    | 'property-catalog'
    | 'inventory'
    | 'marketplace';

/** @deprecated Prefer AddItemsCatalogTab */
export type OrdersAddItemsCatalogTab = AddItemsCatalogTab;

export type CatalogIdentity =
    | { flow: 'legacy' }
    | { flow: 'orders-add-items'; tab: AddItemsCatalogTab }
    | { flow: 'lists-add-items'; tab: AddItemsCatalogTab };

const CatalogFlowContext = createContext<CatalogFlow>('legacy');

interface CatalogIdentityProviderProps {
    value: CatalogFlow;
    children: ReactNode;
}

export const CatalogIdentityProvider = ({
    value,
    children,
}: CatalogIdentityProviderProps) => (
    <CatalogFlowContext.Provider value={value}>
        {children}
    </CatalogFlowContext.Provider>
);

/**
 * Resolves add-items tab from the URL using shared `/add-items/...` suffixes
 * (orders and custom-list flows).
 */
export const getCurrentTabFromPathname = (
    pathname: string,
): AddItemsCatalogTab => {
    if (pathname.includes('/add-items/marketplace')) {
        return 'marketplace';
    }

    if (pathname.includes('/add-items/inventory')) {
        return 'inventory';
    }

    if (pathname.includes('/add-items/categories')) {
        return 'property-catalog';
    }

    return 'property-catalog';
};

export const useCatalogIdentity = (): CatalogIdentity => {
    const flow = useContext(CatalogFlowContext);
    const { pathname } = useLocation();

    if (flow === 'legacy') {
        return { flow: 'legacy' };
    }

    return {
        flow,
        tab: getCurrentTabFromPathname(pathname),
    };
};

export const useIsOrdersAddItemsFlow = (): boolean => {
    const flow = useContext(CatalogFlowContext);
    return flow === 'orders-add-items';
};

export const useIsListsAddItemsFlow = (): boolean => {
    const flow = useContext(CatalogFlowContext);
    return flow === 'lists-add-items';
};

export const useIsAddItemsFlow = (): boolean => {
    const flow = useContext(CatalogFlowContext);
    return flow === 'orders-add-items' || flow === 'lists-add-items';
};

export const useIsOrdersAddItemsMarketplace = () => {
    const flow = useContext(CatalogFlowContext);
    const { pathname } = useLocation();
    return (
        (flow === 'orders-add-items' || flow === 'lists-add-items') &&
        getCurrentTabFromPathname(pathname) === 'marketplace'
    );
};
